import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import InputMask from 'react-input-mask';
import { getTrackBackground, Range } from 'react-range';
import styles from './index.module.scss';
import HeadContainer from '../head-container';
import { getImageFromS3 } from '../../imageService';
import apiService from '../../utils/apiService';
import { executeRecaptchaV3, loadRecaptchaV3 } from '../../utils/recaptchaV3';
import { getVisitorId, isVisitorLimitError } from '../../utils/fingerprint';
import {
  CheckIcon,
  PhoneErrorIcon,
  PlayIcon,
  RemoveIcon,
  UploadIcon
} from './icons';
import OtpComponent from './components/otp-component';
import ResultComponent from './components/result-component';
import AttemptsExhaustedComponent from './components/attempts-exhausted-component';

export const NEW_REVIEW_SCREENS = {
  FORM: 0,
  OTP: 1,
  SUCCESS: 2,
  ALREADY_USED: 3,
  ATTEMPTS_EXHAUSTED: 4
};

const MAX_FILES = 10;
const MAX_NAME_LENGTH = 30;
const RECAPTCHA_V3_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
const RECAPTCHA_ACTION = 'feedback_action';
const OTP_STATUSES = ['PENDING_OTP', 'PENDING_CDA_OTP'];

const getProjectPath = (isQrCode) => {
  const productMatch = isQrCode?.match(/\d{2}UA[^?& \s]*/);

  if (productMatch) return productMatch[0];

  return isQrCode?.includes('utm_campaign=')
    ? isQrCode.split('utm_campaign=')[1].split('&')[0]
    : isQrCode;
};

const RangeItem = ({ labelText, rangeValue, setRangeValue }) => (
  <>
    <label className={styles.rangeTitle}>{labelText}</label>
    <Range
      step={1}
      min={1}
      max={5}
      values={rangeValue}
      ariaLabel={labelText}
      renderTrack={({ props, children }) => (
        <div
          {...props}
          style={{
            ...props.style,
            display: 'flex',
            position: 'relative',
            height: '22px',
            width: '100%',
            background: getTrackBackground({
              values: rangeValue,
              colors: ['#0058DD', '#041942'],
              min: 1,
              max: 5
            }),
            borderRadius: '4px'
          }}
        >
          {children}
        </div>
      )}
      renderThumb={({ props }) => <div {...props} className={styles.thumb} />}
      onChange={(values) => setRangeValue(values)}
    />
    <div className={styles.rangeItemList}>
      <span>1</span>
      <span>2</span>
      <span>3</span>
      <span>4</span>
      <span>5</span>
    </div>
  </>
);

const NewReviewFlowScreen = ({ setScreenNumber }) => {
  const [screen, setScreen] = useState(NEW_REVIEW_SCREENS.FORM);

  const [designRangeValue, setDesignRangeValue] = useState([5]);
  const [tasteRangeValue, setTasteRangeValue] = useState([1]);

  const [comment, setComment] = useState('');
  const [isCommentError, setIsCommentError] = useState(false);

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isFilesError, setIsFilesError] = useState(false);

  const [phone, setPhone] = useState('');
  const [isPhoneError, setIsPhoneError] = useState(false);
  const [name, setName] = useState('');
  const [isNameError, setIsNameError] = useState(false);

  const [isCheckedCallback, setIsCheckedCallback] = useState(false);
  const [isCheckedPolicy, setIsCheckedPolicy] = useState(false);
  const [isPolicyError, setIsPolicyError] = useState(false);

  const [feedbackId, setFeedbackId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const commentRef = useRef(null);
  const filesRef = useRef(null);
  const phoneRef = useRef(null);
  const nameRef = useRef(null);
  const policyRef = useRef(null);

  useEffect(() => {
    loadRecaptchaV3(RECAPTCHA_V3_SITE_KEY);
  }, []);

  const sanitizeComment = (value) =>
    (value || '').replace(/[^A-Za-zА-Яа-яІЇЄҐіїєґ’' -]/g, '');

  const sanitizeName = (value) =>
    (value || '')
      .replace(/[^A-Za-zА-Яа-яЁёІіЇїЄєҐґ0-9 '’.,!?:;()-]/g, '')
      .slice(0, MAX_NAME_LENGTH);

  const handleUploadFile = (event) => {
    const selected = Array.from(event.target.files || []).slice(
      0,
      MAX_FILES - files.length
    );

    if (selected.length === 0) return;

    setIsFilesError(false);
    setFiles((prev) => [...prev, ...selected].slice(0, MAX_FILES));
    setPreviews((prev) =>
      [
        ...prev,
        ...selected.map((file) => ({
          url: URL.createObjectURL(file),
          isVideo: (file.type || '').startsWith('video')
        }))
      ].slice(0, MAX_FILES)
    );

    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
    if (isSubmitting) return;

    const isPhoneValid = /^\(\d{2}\) \d{3}-\d{2}-\d{2}$/.test(phone);
    const isCommentValid = comment.trim().length > 0;
    const isNameValid = name.trim().length > 0;

    setIsCommentError(!isCommentValid);
    setIsFilesError(files.length === 0);
    setIsPhoneError(!isPhoneValid);
    setIsNameError(!isNameValid);
    setIsPolicyError(!isCheckedPolicy);

    const isFormValid =
      isCommentValid &&
      files.length > 0 &&
      isPhoneValid &&
      isNameValid &&
      isCheckedPolicy;

    if (!isFormValid) {
      const firstErrorRef = [
        [isCommentValid, commentRef],
        [files.length > 0, filesRef],
        [isPhoneValid, phoneRef],
        [isNameValid, nameRef],
        [isCheckedPolicy, policyRef]
      ].find(([isValid]) => !isValid)?.[1];

      firstErrorRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      return;
    }

    const isQrCode = sessionStorage.getItem('isQrCode');
    const projectPath = getProjectPath(isQrCode);
    const fullPhone = `380${phone.replace(/\D/g, '')}`;

    const formData = new FormData();

    files.forEach((file) => formData.append('files', file));

    formData.append('phone', fullPhone);
    formData.append('name', name.trim().replace(/’/g, "'"));
    formData.append('registration', true);
    formData.append('comment', comment);
    formData.append('design', designRangeValue[0]);
    formData.append('flavor', tasteRangeValue[0]);
    formData.append('callback', isCheckedCallback);
    formData.append('link', `https://yourjourney.com.ua/${projectPath}`);
    formData.append('sku_code', projectPath || isQrCode);
    formData.append('channel', 'YourJourney');

    try {
      setIsSubmitting(true);
      setSubmitError('');

      const visitorId = await getVisitorId();
      if (visitorId) formData.append('visitor_id', visitorId);

      const recaptchaToken = RECAPTCHA_V3_SITE_KEY
        ? await executeRecaptchaV3(RECAPTCHA_V3_SITE_KEY, RECAPTCHA_ACTION)
        : null;

      const headers = { 'Content-Type': 'multipart/form-data' };
      if (recaptchaToken) {
        headers['recaptcha-token'] = recaptchaToken;
        headers['recaptcha-action'] = RECAPTCHA_ACTION;
      }

      const {
        data: { id, status }
      } = await apiService.post('feedback', { data: formData, headers });

      setFeedbackId(id);
      setScreen(
        OTP_STATUSES.includes(status)
          ? NEW_REVIEW_SCREENS.OTP
          : NEW_REVIEW_SCREENS.ALREADY_USED
      );
    } catch (e) {
      if (isVisitorLimitError(e)) {
        setScreen(NEW_REVIEW_SCREENS.ALREADY_USED);
      } else if (e?.response?.status === 400) {
        setSubmitError('Перевір, будь ласка, введені дані');
      } else {
        setSubmitError('Щось пішло не так. Спробуй ще раз');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (screen === NEW_REVIEW_SCREENS.OTP) {
    return (
      <OtpComponent
        setScreenNumber={setScreenNumber}
        feedbackId={feedbackId}
        phoneNumber={phone ? `380${phone.replace(/\D/g, '')}` : ''}
        handleChangePhoneNumber={() => setScreen(NEW_REVIEW_SCREENS.FORM)}
        successSendOtp={() => setScreen(NEW_REVIEW_SCREENS.SUCCESS)}
        attemptsExhausted={() =>
          setScreen(NEW_REVIEW_SCREENS.ATTEMPTS_EXHAUSTED)
        }
      />
    );
  }

  if (screen === NEW_REVIEW_SCREENS.ATTEMPTS_EXHAUSTED) {
    return (
      <AttemptsExhaustedComponent
        setScreenNumber={setScreenNumber}
        handleCheckNumberClick={() => setScreen(NEW_REVIEW_SCREENS.FORM)}
      />
    );
  }

  if (
    screen === NEW_REVIEW_SCREENS.SUCCESS ||
    screen === NEW_REVIEW_SCREENS.ALREADY_USED
  ) {
    return (
      <ResultComponent
        setScreenNumber={setScreenNumber}
        isSuccess={screen === NEW_REVIEW_SCREENS.SUCCESS}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <HeadContainer setScreenNumber={setScreenNumber} />
        <p className={styles.title}>
          Нам дуже важливий твій відгук про продукт, щоб стати краще
        </p>
        <p className={styles.subtitle}>
          Залишай відгук і отримуй поповнення мобільного рахунку на{' '}
          <b>30 грн</b> за приділений час
        </p>
        <p className={styles.bonusBadge}>
          опція поповнення доступна раз на місяць
        </p>

        <RangeItem
          labelText="Як тобі дизайн пачки?"
          rangeValue={designRangeValue}
          setRangeValue={setDesignRangeValue}
        />
        <RangeItem
          labelText="Оціни смакові властивості"
          rangeValue={tasteRangeValue}
          setRangeValue={setTasteRangeValue}
        />

        <div className={styles.commentContainer} ref={commentRef}>
          <label htmlFor="new-review-comment">Твій коментар:</label>
          <textarea
            id="new-review-comment"
            className={classNames(isCommentError && styles.textAreaError)}
            value={comment}
            maxLength={1000}
            onChange={(event) =>
              setComment(sanitizeComment(event.target.value).slice(0, 1000))
            }
            onFocus={() => setIsCommentError(false)}
            placeholder="Залиш свій коментар"
          />
          {isCommentError && (
            <p className={styles.textError}>Обов&#39;язкове поле</p>
          )}
        </div>

        <div className={styles.uploadContainer} ref={filesRef}>
          <label htmlFor="new-review-upload">
            Додай фото продукту (можна декілька):
          </label>
          <input
            id="new-review-upload"
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,image/jpeg,image/png,video/*"
            onChange={handleUploadFile}
          />
          <label
            htmlFor="new-review-upload"
            className={classNames(
              styles.dropZone,
              isFilesError && styles.dropZoneError
            )}
          >
            <UploadIcon />
            <span className={styles.dropZoneText}>
              Завантажте зображення сюди
            </span>
            <span className={styles.dropZoneButton}>Завантажити</span>
          </label>
          {isFilesError && (
            <p className={styles.textError}>Обов&#39;язкове поле</p>
          )}
        </div>

        {previews.length > 0 && (
          <div className={styles.previewList}>
            {previews.map((preview, index) => (
              <div className={styles.previewItem} key={preview.url}>
                {preview.isVideo ? (
                  <video src={preview.url} muted playsInline />
                ) : (
                  <img src={preview.url} alt={`preview-${index}`} />
                )}
                {preview.isVideo && (
                  <i className={styles.playIcon}>
                    <PlayIcon />
                  </i>
                )}
                <i
                  className={styles.removeIcon}
                  onClick={() => handleRemoveFile(index)}
                >
                  <RemoveIcon />
                </i>
              </div>
            ))}
          </div>
        )}

        <div className={styles.infoContainer}>
          <p>
            Додай фото пачки, що допоможуть ідентифікувати проблему та партію
            продукту. Нижче приклад, головне — це чітке зображення інформації на
            дні пачки.
          </p>
          <div className={styles.infoImage}>
            <img
              src={getImageFromS3('images/review-info.png')}
              alt="review-info"
            />
          </div>
        </div>

        <p className={styles.formTitle}>Заповни, будь ласка, свої дані</p>

        <div className={styles.phoneLabels}>
          <span>Код</span>
          <span>
            Номер телефону<sup>*</sup>
          </span>
        </div>
        <div className={styles.phoneRow} ref={phoneRef}>
          <div className={styles.phoneCode}>+380</div>
          <div className={styles.phoneInputBox}>
            <InputMask
              mask="(99) 999-99-99"
              maskChar="_"
              alwaysShowMask
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              onFocus={() => setIsPhoneError(false)}
              className={classNames(
                styles.phoneInput,
                isPhoneError && styles.inputError
              )}
            >
              {(inputProps) => <input {...inputProps} type="tel" />}
            </InputMask>
            {isPhoneError && (
              <i className={styles.phoneErrorIcon}>
                <PhoneErrorIcon />
              </i>
            )}
          </div>
        </div>
        {isPhoneError && (
          <p className={styles.textError}>Невірний формат номеру</p>
        )}

        <p className={styles.nameLabel}>Ім’я</p>
        <input
          type="text"
          ref={nameRef}
          className={classNames(
            styles.nameInput,
            isNameError && styles.inputError
          )}
          value={name}
          maxLength={MAX_NAME_LENGTH}
          placeholder="Введи своє ім’я"
          onChange={(event) => setName(sanitizeName(event.target.value))}
          onFocus={() => setIsNameError(false)}
        />
        {isNameError && (
          <p className={styles.textError}>Обов&#39;язкове поле</p>
        )}

        <div
          className={styles.checkboxRow}
          onClick={() => setIsCheckedCallback(!isCheckedCallback)}
        >
          <span className={styles.checkbox}>
            {isCheckedCallback && <CheckIcon />}
          </span>
          <span className={styles.checkboxLabel}>
            Хочу отримати зворотній зв&#39;язок 😊
          </span>
        </div>

        <div
          className={styles.checkboxRow}
          ref={policyRef}
          onClick={() => {
            setIsCheckedPolicy(!isCheckedPolicy);
            setIsPolicyError(false);
          }}
        >
          <span
            className={classNames(
              styles.checkbox,
              isPolicyError && styles.checkboxError
            )}
          >
            {isCheckedPolicy && <CheckIcon />}
          </span>
          <span className={styles.checkboxLabel}>
            Я погоджуюсь з{' '}
            <a
              href="https://yourjourney.com.ua/personal-data-policy"
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
            >
              положенням про конфіденційність та захист персональних даних
            </a>{' '}
            та підтверджую, що є споживачем тютюнових виробів та/або
            користувачем пристроїв для споживання тютюнових виробів без їх
            згоряння
          </span>
        </div>

        {submitError && <p className={styles.submitError}>{submitError}</p>}

        <button
          className={classNames(
            styles.blueButton,
            isSubmitting && styles.blueButtonDisabled
          )}
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          <span>Залишити відгук</span>
        </button>
        <button
          className={styles.whiteButton}
          onClick={() => setScreenNumber(1)}
        >
          Повернутися до меню
        </button>
      </div>
    </div>
  );
};

export default NewReviewFlowScreen;
